import { useEffect, useRef, useState } from 'react';
import Hls from 'hls.js';
import { PROXYUrl } from '../../utils/common';

export default function LivePlayer({ streamUrl = '' }) {
  const videoRef = useRef(null);
  const hlsRef = useRef(null);

  const [status, setStatus] = useState('loading'); // loading | live | error

  useEffect(() => {
    const video = videoRef.current;

    if (!video || !streamUrl) return;

    setStatus('loading');

    const proxied = PROXYUrl + encodeURIComponent(streamUrl);

    // Safari Native HLS
    if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = proxied;

      video.addEventListener('loadedmetadata', () => {
        setStatus('live');
      });

      video.addEventListener('waiting', () => {
        setStatus('loading');
      });

      video.addEventListener('playing', () => {
        setStatus('live');
      });

      video.addEventListener('error', () => {
        setStatus('error');
      });

      return;
    }

    if (!Hls.isSupported()) {
      setStatus('error');
      return;
    }

    const hls = new Hls({
      lowLatencyMode: true,
    });

    hlsRef.current = hls;

    hls.loadSource(proxied);
    hls.attachMedia(video);

    hls.on(Hls.Events.MANIFEST_PARSED, () => {
      video.play().catch(() => {});
    });

    hls.on(Hls.Events.BUFFER_APPENDING, () => {
      setStatus('loading');
    });

    video.addEventListener('playing', () => {
      setStatus('live');
    });

    video.addEventListener('waiting', () => {
      setStatus('loading');
    });

    hls.on(Hls.Events.ERROR, (_, data) => {
      if (data.fatal) {
        setStatus('error');
      }
    });

    return () => {
      hls.destroy();
    };
  }, [streamUrl]);

  return (
    <div
      className="mb-4 h-full position-relative"
      style={{
        background: '#000',
        borderRadius: 8,
        overflow: 'hidden',
      }}
    >
      {/* Loader */}
      {status === 'loading' && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(0,0,0,0.5)',
            zIndex: 10,
          }}
        >
          <div
            style={{
              width: 50,
              height: 50,
              border: '4px solid rgba(255,255,255,0.2)',
              borderTop: '4px solid #fff',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
            }}
          />
        </div>
      )}

      {/* Error */}
      {status === 'error' && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            background: 'rgba(0,0,0,0.7)',
            zIndex: 10,
          }}
        >
          Stream Failed
        </div>
      )}

      <video
        ref={videoRef}
        controls
        muted
        autoPlay
        playsInline
        style={{
          width: '100%',
          height: '100%',
          display: 'block',
        }}
      />

      {/* Spinner Animation */}
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
}